/**
 * Showing the assembly at real scale through iOS AR Quick Look.
 *
 * Safari on iOS still does not implement the WebXR AR module, so the scene
 * cannot be composited over the camera from the page. What iOS does offer is
 * AR Quick Look: hand it a USDZ file through a link marked `rel="ar"` and the
 * system viewer places the model on a real surface at true scale.
 *
 * The file itself is built by the server (`POST /api/ar/usdz`). It used to be
 * built here with three's `USDZExporter`, but that exporter writes ASCII usda
 * -- around 150 bytes per triangle -- so a nine-module assembly had to be cut
 * to a 60k-triangle budget before the phone would open it, and cutting CAD
 * links that hard is what tore them into shards. The server writes binary
 * usdc instead and holds the same assembly's full detail (403k triangles) in
 * the same 12 MB, so nothing is decimated in the browser any more.
 *
 * What this module still owns is the part of the export only the page knows:
 * which meshes are the robot, where they are right now, and what colour they
 * are wearing.
 *
 * Real scale comes for free: the assembly is modelled in metres, which is also
 * USD's unit.
 *
 * Note this gives no access to the device's depth sensor. AR Quick Look uses
 * LiDAR internally for placement and occlusion, but the web platform exposes
 * none of it; anything that needs the depth map itself needs a native app.
 */

import * as THREE from "three";

/** Marks the objects that make up the robot, as opposed to the grid, gizmos and markers. */
export const ROBOT_MESH_FLAG = "robotMesh";

/** Where the server builds the USDZ for a scene. */
export const AR_USDZ_ENDPOINT = "/api/ar/usdz";

/**
 * One mesh, as the export request describes it.
 *
 * `matrix` is the mesh's world transform in three's `Matrix4.elements` order,
 * which the server hands straight to USD -- three's column-major layout of a
 * column-vector matrix is byte-for-byte USD's row-major layout of a
 * row-vector one.
 *
 * `color` is the material's colour in the renderer's working space, 0-255 per
 * channel. The server multiplies the mesh's own colours by it, so a part with
 * a URDF material override is tinted and a part without one comes through
 * unchanged.
 */
export interface ArPart {
  glb_url: string;
  matrix: number[];
  color: [number, number, number] | null;
  opacity: number;
  name: string;
}

/**
 * Whether this browser can open a USDZ in AR.
 *
 * `relList.supports` is the feature check Apple documents; it is false on
 * every non-Apple browser, where the link would just download the file.
 */
export function supportsArQuickLook(): boolean {
  if (typeof document === "undefined") {
    return false;
  }
  const anchor = document.createElement("a");
  return Boolean(anchor.relList?.supports?.("ar"));
}

/** The material a part is currently wearing, wherever it hangs in the holder. */
function materialOf(holder: THREE.Object3D): THREE.Material | null {
  const holderMesh = holder as THREE.Mesh;
  if (holderMesh.isMesh) {
    return Array.isArray(holderMesh.material) ? holderMesh.material[0] : holderMesh.material;
  }
  let found: THREE.Material | null = null;
  holder.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!found && mesh.isMesh) {
      found = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
    }
  });
  return found;
}

/**
 * Describe the robot meshes in the scene for the server to export.
 *
 * The live scene also holds the grid, the IK gizmos and the connection point
 * markers, none of which belong in the exported model, so parts are picked by
 * flag rather than the whole scene being sent.
 */
export function collectArParts(scene: THREE.Object3D): ArPart[] {
  scene.updateMatrixWorld(true);
  const parts: ArPart[] = [];
  let unexportable = 0;
  scene.traverse((object) => {
    if (!object.userData?.[ROBOT_MESH_FLAG]) {
      return;
    }
    if (typeof object.userData.glbUrl !== "string") {
      // A mesh the server sent as raw vertices instead of a GLB has no
      // content hash to export by. The builder's parts are all GLBs, so this
      // is worth saying out loud rather than quietly dropping a part.
      unexportable += 1;
      return;
    }
    const material = materialOf(object) as THREE.MeshStandardMaterial | null;
    const color = material?.color;
    parts.push({
      glb_url: object.userData.glbUrl,
      matrix: [...object.matrixWorld.elements],
      color: color
        ? [Math.round(color.r * 255), Math.round(color.g * 255), Math.round(color.b * 255)]
        : null,
      opacity: material?.transparent ? (material.opacity ?? 1) : 1,
      name: typeof object.userData.linkName === "string" ? object.userData.linkName : "part",
    });
  });
  if (unexportable > 0) {
    console.warn(`AR export: ${unexportable} mesh(es) are not GLB-backed and were left out.`);
  }
  return parts;
}

/** Which plane family the file anchors to, and how the robot faces it. */
export type ArMount = "floor" | "wall";

/** Ask the server for the USDZ of these parts, as raw bytes. */
export async function fetchAssemblyUsdz(parts: ArPart[], mount: ArMount = "floor"): Promise<ArrayBuffer> {
  const response = await fetch(AR_USDZ_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ parts, mount }),
  });
  if (!response.ok) {
    // The endpoint answers with {"detail": ...}; anything else is a proxy or
    // a crash, and the status is all there is to report.
    let detail = `The server could not build the AR file (${response.status}).`;
    try {
      const body = await response.json();
      if (typeof body?.detail === "string") {
        detail = body.detail;
      }
    } catch {
      // Not JSON: keep the status message.
    }
    throw new Error(detail);
  }
  return response.arrayBuffer();
}

/**
 * Build the USDZ and hand it to AR Quick Look.
 *
 * The link is clicked from the current page rather than opened in a new tab:
 * a new window makes Safari show an intermediate thumbnail page instead of
 * going straight into AR. It also carries an `<img>` child, which Safari
 * requires before it will treat the anchor as an AR link.
 */
export async function openAssemblyInAr(scene: THREE.Object3D, mount: ArMount = "floor"): Promise<void> {
  const parts = collectArParts(scene);
  if (parts.length === 0) {
    throw new Error("There is nothing in the scene to show in AR yet.");
  }
  const usdz = await fetchAssemblyUsdz(parts, mount);
  // Quick Look goes by the MIME type, so the bytes are wrapped here rather
  // than the response's own blob being trusted to carry it.
  const url = URL.createObjectURL(new Blob([usdz], { type: "model/vnd.usdz+zip" }));

  const link = document.createElement("a");
  link.rel = "ar";
  link.href = url;
  link.style.display = "none";
  link.appendChild(document.createElement("img"));
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Quick Look reads the blob after the click returns, so the URL cannot be
  // revoked immediately; a minute is far longer than the read takes and still
  // bounds how long the buffer is held.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
